import { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
    Box,
    CircularProgress,
    IconButton,
    makeStyles,
    Tooltip,
} from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import BorderColorOutlinedIcon from '@mui/icons-material/BorderColorOutlined';
import FileCopyOutlinedIcon from '@mui/icons-material/FileCopyOutlined';
import Container from '../Elements/Container';
import TopNav from '../common/TopNav';
import AlertComponent from '../Elements/AlertComponent';
import ConfirmationDialog from '../AlertsAndReports/ConfirmationDialog';
import LoadingComponentBlur from '../common/LoadingComponentBlur';
import DiagramVisualSection from './DiagramVisualSection';
import { createDiagram, getDiagramByid, getDiagramList, deleteDiagram } from '../../services/diagrams';
import { getPlc_automate_24v_list } from '../../services/plc_automate';
import { getStoredThumbnailMap, removeStoredThumbnail } from './diagramThumbnailStorage';

const TOP_NAV_TOTAL_HEIGHT = 112;
const DIAGRAM_SELECTION_CARD_SELECTOR = '[data-diagram-selection-card="true"]';
const DIAGRAM_SELECTION_ACTION_SELECTOR = '[data-diagram-selection-preserve="true"]';
const DIALOG_SELECTOR = '[role="dialog"]';

const useStyles = makeStyles(theme => ({
    sectionActions: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-start',
        flexWrap: 'wrap',
        gap: theme.spacing(1),
        width: '100%',
        '@media (max-width: 920px)': {
            justifyContent: 'flex-start',
        },
    },
    sectionSelectionLabel: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: 34,
        height: 34,
        padding: theme.spacing(0, 1.15),
        borderRadius: 999,
        backgroundColor: theme.palette.surface.selected,
        color: theme.palette.primary.main,
        fontSize: 12,
        fontWeight: 700,
        border: `1px solid ${theme.palette.primary.light}`,
    },
    iconActionButton: {
        width: 38,
        height: 38,
        borderRadius: 12,
        border: `1px solid ${theme.palette.border.main}`,
        backgroundColor: theme.palette.common.white,
        color: theme.palette.primary.main,
        boxShadow: theme.palette.shadow.card,
        transition: 'transform 160ms ease, box-shadow 160ms ease, border-color 160ms ease, background-color 160ms ease',
        '&:hover': {
            backgroundColor: theme.palette.surface.selected,
            borderColor: theme.palette.primary.light,
            boxShadow: theme.palette.shadow.sticky,
            transform: 'translateY(-1px)',
        },
        '&.Mui-disabled': {
            color: theme.palette.text.disabled,
            backgroundColor: theme.palette.action.disabledBackground,
            borderColor: theme.palette.action.disabledBackground,
            boxShadow: 'none',
        },
    },
    iconActionButtonDelete: {
        color: theme.palette.error.main,
        '&:hover': {
            backgroundColor: '#ffe4e6',
            borderColor: '#fecdd3',
        },
    },
    page: {
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        padding: theme.spacing(2, 1.5, 0, 0),
        background: theme.palette.surface.page,
        height: `calc(100vh - ${TOP_NAV_TOTAL_HEIGHT}px)`,
        maxHeight: `calc(100vh - ${TOP_NAV_TOTAL_HEIGHT}px)`,
        boxSizing: 'border-box',
        overflow: 'hidden',
        '@media (max-width: 920px)': {
            padding: theme.spacing(1.5, 0, 0),
        },
    },
    contentGrid: {
        display: 'grid',
        gridTemplateColumns: '1.15fr 1fr',
        gap: theme.spacing(1.25),
        alignItems: 'stretch',
        flex: 1,
        minHeight: 0,
        '@media (max-width: 1220px)': {
            gridTemplateColumns: '1fr',
            gridTemplateRows: 'minmax(0, 1fr) minmax(0, 1fr)',
        },
    },
}));

const Diagrams = () => {
    const classes = useStyles();
    const history = useHistory();
    const [loading, setLoading] = useState(true);
    const [msg, setMsg] = useState({
        msg: "",
        type: "error"
    });
    const [close, setClose] = useState(false);
    const [dataDiagramList, setDataDiagramList] = useState([]);
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const [selectedDiagramIds, setSelectedDiagramIds] = useState([]);
    const [diagramIdsPendingDelete, setDiagramIdsPendingDelete] = useState([]);
    const [loadingSave, setLoadingSave] = useState(false);
    const [plcList, setPlcListt] = useState([]);
    const [diagramThumbnailMap, setDiagramThumbnailMap] = useState({});
    const [automateThumbnailMap, setAutomateThumbnailMap] = useState({});
    const [thumbnailMapsLoading, setThumbnailMapsLoading] = useState(false);
    const automateListWithMock = useMemo(() => plcList, [plcList]);

    useEffect(() => {
        getDiagramListFtn();
        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        setSelectedDiagramIds(previousState =>
            previousState.filter(id => dataDiagramList.some(diagram => diagram?.id === id))
        );
    }, [dataDiagramList]);

    useEffect(() => {
        if (selectedDiagramIds.length === 0) {
            return undefined;
        }

        const handleClearSelectionOnOutsideClick = event => {
            const target = event.target;

            if (!(target instanceof Element)) {
                return;
            }

            if (
                target.closest(DIAGRAM_SELECTION_CARD_SELECTOR)
                || target.closest(DIAGRAM_SELECTION_ACTION_SELECTOR)
                || target.closest(DIALOG_SELECTOR)
            ) {
                return;
            }

            setSelectedDiagramIds([]);
        };

        document.addEventListener('click', handleClearSelectionOnOutsideClick);

        return () => {
            document.removeEventListener('click', handleClearSelectionOnOutsideClick);
        };
    }, [selectedDiagramIds]);

    useEffect(() => {
        const getPlcAutomateList = async () => {
            let res = await getPlc_automate_24v_list();
            if (res?.data) {
                setPlcListt(res?.data);
            }
            setLoading(false);
        };

        getPlcAutomateList();
    }, []);

    useEffect(() => {
        let active = true;

        const loadStoredThumbnails = async () => {
            if (!dataDiagramList.length && !plcList.length) {
                if (active) {
                    setDiagramThumbnailMap({});
                    setAutomateThumbnailMap({});
                    setThumbnailMapsLoading(false);
                }
                return;
            }

            try {
                if (active) {
                    setThumbnailMapsLoading(true);
                }
                const [diagramMap, automateMap] = await Promise.all([
                    getStoredThumbnailMap({
                        ownerIds: dataDiagramList.map(diagram => diagram?.id),
                        ownerType: 'diagram',
                    }),
                    getStoredThumbnailMap({
                        ownerIds: plcList.map(automate => automate?.id),
                        ownerType: 'automate',
                    }),
                ]);

                if (active) {
                    setDiagramThumbnailMap(diagramMap);
                    setAutomateThumbnailMap(automateMap);
                }
            } catch (error) {
                if (active) {
                    setDiagramThumbnailMap({});
                    setAutomateThumbnailMap({});
                }
            } finally {
                if (active) {
                    setThumbnailMapsLoading(false);
                }
            }
        };

        loadStoredThumbnails();

        return () => {
            active = false;
        };
    }, [dataDiagramList, plcList]);

    const handleDirectionDiagramPlc = async idAux => {
        history.push(`/plc/${idAux}`);
    };

    const getDiagramListFtn = async () => {
        try {
            let res = await getDiagramList();
            if (res?.data?.results) {
                setDataDiagramList(res?.data?.results);
            }
        }
        catch (error) {
            let errorMsg = '';
            if (error?.response?.data?.msg) {
                errorMsg = `${error?.response?.data?.msg}.`;
            }
            else {
                errorMsg = 'Ocurrio un error inesperado';
            }
            setMsg({
                msg: errorMsg,
                type: "error"
            });
        }
        setLoading(false);
    };

    const handleCreateDiagram = () => {
        history.push('/create-diagram');
    };

    const handleDirectionDiagram = async idAux => {
        history.push(`/diagram/${idAux}`);
    };

    const handleToggleDiagramSelection = (diagram) => {
        if (!diagram?.id) {
            return;
        }

        setSelectedDiagramIds(previousState =>
            previousState.includes(diagram.id)
                ? previousState.filter(id => id !== diagram.id)
                : [...previousState, diagram.id]
        );
    };

    const handleEditSelectedDiagram = () => {
        if (selectedDiagramIds.length !== 1) {
            return;
        }

        handleDirectionDiagram(selectedDiagramIds[0]);
    };

    const buildDuplicateDiagramName = (originalName, usedNames) => {
        const safeBaseName = originalName?.trim() || 'Diagrama sin nombre';
        const baseDuplicateName = `Copia de ${safeBaseName}`;

        if (!usedNames.has(baseDuplicateName)) {
            usedNames.add(baseDuplicateName);
            return baseDuplicateName;
        }

        let suffix = 2;
        let candidateName = `${baseDuplicateName} (${suffix})`;

        while (usedNames.has(candidateName)) {
            suffix += 1;
            candidateName = `${baseDuplicateName} (${suffix})`;
        }

        usedNames.add(candidateName);
        return candidateName;
    };

    const handleDuplicateSelectedDiagrams = async () => {
        if (selectedDiagramIds.length === 0) {
            return;
        }

        setLoadingSave(true);
        try {
            const usedNames = new Set(
                dataDiagramList
                    .map(diagram => diagram?.name || diagram?.nombre)
                    .filter(Boolean)
            );
            const duplicatedDiagramIds = [];

            for (const diagramId of selectedDiagramIds) {
                const response = await getDiagramByid(diagramId);
                const sourceDiagram = response?.data;

                if (!sourceDiagram) {
                    continue;
                }

                const duplicateName = buildDuplicateDiagramName(sourceDiagram?.name, usedNames);
                const body = {
                    type_plc: sourceDiagram?.type_plc || 'Automate-24V-4M',
                    name: duplicateName,
                    edges: sourceDiagram?.edges || [],
                    nodes: sourceDiagram?.nodes || [],
                };
                const duplicateResponse = await createDiagram(body);
                const newDiagramId = duplicateResponse?.data?.id;

                if (newDiagramId) {
                    duplicatedDiagramIds.push(newDiagramId);
                }
            }

            await getDiagramListFtn();
            setSelectedDiagramIds(duplicatedDiagramIds);
            setMsg({
                msg: selectedDiagramIds.length === 1
                    ? 'Se duplico el diagrama seleccionado.'
                    : 'Se duplicaron los diagramas seleccionados.',
                type: 'success',
            });
            setClose(true);
        }
        catch (error) {
            let errorMsg = '';
            if (error?.response?.data?.msg) {
                errorMsg = `${error?.response?.data?.msg}.`;
            }
            else {
                errorMsg = 'Ocurrio un error inesperado';
            }
            setMsg({
                msg: errorMsg,
                type: 'error',
            });
            setClose(true);
        }
        setLoadingSave(false);
    };

    const handleOpenDeleteDialog = (diagramIds = []) => {
        if (!diagramIds.length) {
            return;
        }

        setDiagramIdsPendingDelete(diagramIds);
        setIsDeleteDialogOpen(true);
    };

    const handleDelete = async () => {
        try {
            setIsDeleteDialogOpen(false);
            setLoadingSave(true);
            for (const diagramId of diagramIdsPendingDelete) {
                await deleteDiagram(diagramId);
                try {
                    await removeStoredThumbnail({
                        ownerId: diagramId,
                        ownerType: 'diagram',
                    });
                } catch (thumbnailError) {
                    console.error(thumbnailError);
                }
            }
            setDiagramThumbnailMap(previousState => {
                const nextState = { ...previousState };
                diagramIdsPendingDelete.forEach(diagramId => {
                    delete nextState[diagramId];
                });
                return nextState;
            });
            setSelectedDiagramIds(previousState =>
                previousState.filter(id => !diagramIdsPendingDelete.includes(id))
            );
            setDiagramIdsPendingDelete([]);
            await getDiagramListFtn();
            setMsg({
                msg: diagramIdsPendingDelete.length === 1
                    ? 'Se elimino el diagrama seleccionado.'
                    : 'Se eliminaron los diagramas seleccionados.',
                type: 'success',
            });
            setClose(true);
        }
        catch (error) {
            let errorMsg = '';
            if (error?.response?.data?.msg) {
                errorMsg = `${error?.response?.data?.msg}.`;
            }
            else {
                errorMsg = 'Ocurrio un error inesperado';
            }
            setMsg({
                msg: errorMsg,
                type: "error"
            });
            setClose(true);
        }
        setLoadingSave(false);
    };

    const panelHeaderActions = (
        <Box className={classes.sectionActions}>
            {selectedDiagramIds.length > 0 && (
                <Box className={classes.sectionSelectionLabel}>
                    {selectedDiagramIds.length}
                </Box>
            )}

            <Tooltip title='Eliminar diagramas seleccionados' arrow placement='top'>
                <span>
                    <IconButton
                        className={`${classes.iconActionButton} ${classes.iconActionButtonDelete}`}
                        onClick={() => handleOpenDeleteDialog(selectedDiagramIds)}
                        disabled={selectedDiagramIds.length === 0}
                        aria-label='Eliminar diagramas seleccionados'
                        data-diagram-selection-preserve='true'
                    >
                        <DeleteOutlineIcon fontSize='small' />
                    </IconButton>
                </span>
            </Tooltip>

            <Tooltip title='Editar diagrama seleccionado' arrow placement='top'>
                <span>
                    <IconButton
                        className={classes.iconActionButton}
                        onClick={handleEditSelectedDiagram}
                        disabled={selectedDiagramIds.length !== 1}
                        aria-label='Editar diagrama seleccionado'
                        data-diagram-selection-preserve='true'
                    >
                        <BorderColorOutlinedIcon fontSize='small' />
                    </IconButton>
                </span>
            </Tooltip>

            <Tooltip title='Duplicar diagramas seleccionados' arrow placement='top'>
                <span>
                    <IconButton
                        className={classes.iconActionButton}
                        onClick={handleDuplicateSelectedDiagrams}
                        disabled={selectedDiagramIds.length === 0}
                        aria-label='Duplicar diagramas seleccionados'
                        data-diagram-selection-preserve='true'
                    >
                        <FileCopyOutlinedIcon fontSize='small' />
                    </IconButton>
                </span>
            </Tooltip>

            <Tooltip title='Nuevo diagrama' arrow placement='top'>
                <span>
                    <IconButton
                        className={classes.iconActionButton}
                        onClick={handleCreateDiagram}
                        aria-label='Nuevo diagrama'
                        data-diagram-selection-preserve='true'
                    >
                        <AddIcon fontSize='small' />
                    </IconButton>
                </span>
            </Tooltip>
        </Box>
    );

    return (
        <>
            <LoadingComponentBlur loadingState={loadingSave} />

            <TopNav titulo="Diagramas" />

            {loading && (
                <Box display="flex" justifyContent="center" marginTop={5}>
                    <CircularProgress />
                </Box>
            )}

            <AlertComponent
                severity={msg.type}
                msg={msg.msg}
                setClose={setClose}
                close={close}
            />

            <ConfirmationDialog
                isOpen={isDeleteDialogOpen}
                handleConfirm={handleDelete}
                handleCancel={() => {
                    setIsDeleteDialogOpen(false);
                    setDiagramIdsPendingDelete([]);
                }}
                title={diagramIdsPendingDelete.length > 1
                    ? 'Desea eliminar los diagramas seleccionados? Este proceso es irreversible'
                    : 'Desea eliminar su diagrama? Este proceso es irreversible'}
                content={diagramIdsPendingDelete.length > 1
                    ? 'En caso de confirmar los diagramas seran eliminados'
                    : 'En caso de confirmar el diagrama sera eliminado'}
            />

            {!loading && (
                <Container>
                    <Box className={classes.page}>
                        <Box className={classes.contentGrid}>
                            <DiagramVisualSection
                                type='diagram'
                                title='Diagramas'
                                subtitle='Administra y edita los diagramas del sistema.'
                                items={dataDiagramList}
                                previewLimit={9}
                                onPrimaryAction={handleDirectionDiagram}
                                diagramThumbnailMap={diagramThumbnailMap}
                                automateThumbnailMap={automateThumbnailMap}
                                thumbnailsLoading={thumbnailMapsLoading}
                                headerActions={panelHeaderActions}
                                selectedDiagramIds={selectedDiagramIds}
                                onSelectDiagram={handleToggleDiagramSelection}
                            />

                            <DiagramVisualSection
                                type='automate'
                                title='Dispositivos Automate'
                                subtitle='Dispositivos que tienen diagramas asociados.'
                                items={automateListWithMock}
                                onPrimaryAction={handleDirectionDiagramPlc}
                                dataDiagramFreeList={dataDiagramList}
                                diagramThumbnailMap={diagramThumbnailMap}
                                automateThumbnailMap={automateThumbnailMap}
                                thumbnailsLoading={thumbnailMapsLoading}
                            />
                        </Box>
                    </Box>
                </Container>
            )}
        </>
    );
};

export default Diagrams;
